import { ExternalUserReservation } from '../../../types/reservation';
import { ReservationType } from '../../../types/reservationType';
import { ReservationForm } from '../validators';
import { ExternalUserWithCancellation, ReservationEvent } from '../types';

/** Splits the form into the fields that need a transformation and the ones that go to the backend as they are. */
const splitReservationForm = (form: ReservationForm, isUnregisteredClient: boolean) => {
  const {
    externalUsers,
    confirmedExternalUsers,
    branch,
    user,
    selectedDay,
    useCoupon,
    userCouponId,
    newCouponId,
    reservationTypeId,
    localStartDate,
    localEndDate,
    unregisteredClientName,
    unregisteredClientSurnames,
    unregisteredClientPhone,
    usersBeforeSave,
    queuedExternalUsers,
    attendanceData,
    typeOfCouponToUse,
    selectedUserToShareCoupon,
    title,
    informativeUser,
    ...rest
  } = form;

  const useExistingOrShareCoupon =
    useCoupon && (typeOfCouponToUse === 'existing' || typeOfCouponToUse === 'share' || typeOfCouponToUse === undefined);

  let unregisteredClient = undefined;
  if (isUnregisteredClient) {
    unregisteredClient = {
      name: unregisteredClientName,
      surnames: unregisteredClientSurnames,
      phone: unregisteredClientPhone,
    };
  }

  return {
    rest,
    externalUsers,
    branch,
    user,
    useCoupon,
    userCouponId,
    newCouponId,
    usersBeforeSave,
    queuedExternalUsers,
    typeOfCouponToUse,
    title,
    informativeUser,
    useExistingOrShareCoupon,
    unregisteredClient,
  };
};

interface EditBodyOptions {
  isUnregisteredClient: boolean;
  canceledUsers: ExternalUserWithCancellation[];
  initialTemporaryEvent: ReservationEvent | undefined;
  /** The reservation type of the selected branch, when a branch is selected. */
  reservationTypeForEditedBranch: ReservationType | undefined;
}

/** The body of `PATCH /reservations/:id`. */
export const getEditReservationBody = (
  form: ReservationForm,
  { isUnregisteredClient, canceledUsers, initialTemporaryEvent, reservationTypeForEditedBranch }: EditBodyOptions,
) => {
  const {
    rest,
    externalUsers,
    branch,
    user,
    useCoupon,
    userCouponId,
    newCouponId,
    usersBeforeSave,
    queuedExternalUsers,
    typeOfCouponToUse,
    title,
    informativeUser,
    useExistingOrShareCoupon,
    unregisteredClient,
  } = splitReservationForm(form, isUnregisteredClient);

  const externalUsersToSend = externalUsers
    ?.map((user) => {
      const newUser: ExternalUserReservation = {
        externalUserId: user.id,
      };
      if (canceledUsers.some((u) => u.id === user.id)) {
        newUser.cancellation = canceledUsers.find((u) => u.id === user.id)?.cancellation;
      }
      return newUser;
    })
    .filter((user) => !queuedExternalUsers?.some((u) => u.id === user.externalUserId && !user.cancellation));

  return {
    ...rest,
    userId: user?.id,
    branchId: branch?.id,
    ...(reservationTypeForEditedBranch ? { reservationTypeId: reservationTypeForEditedBranch.id } : {}),
    userCouponId: useExistingOrShareCoupon ? userCouponId : undefined,
    newCouponId: useCoupon && typeOfCouponToUse === 'new' ? newCouponId : undefined,
    utcStartDate: form.localStartDate ?? initialTemporaryEvent?.start,
    utcEndDate: form.localEndDate ?? initialTemporaryEvent?.end,
    externalUsers: isUnregisteredClient
      ? undefined
      : externalUsersToSend?.concat(usersBeforeSave?.map((user) => ({ externalUserId: user.id })) ?? []),
    ...(isUnregisteredClient ? { unregisteredClient } : {}),
    ...(title !== undefined ? { title } : {}),
    // null clears the informative employee on the backend
    informativeUserId: informativeUser?.id ?? null,
  };
};

interface CreateBodyOptions {
  isUnregisteredClient: boolean;
  isCreating: boolean;
  reservationTypeForSelectedBranch: ReservationType;
}

/** The body of `POST /reservations` for a normal (not blocked) reservation. */
export const getCreateReservationBody = (
  form: ReservationForm,
  { isUnregisteredClient, isCreating, reservationTypeForSelectedBranch }: CreateBodyOptions,
) => {
  const {
    rest,
    externalUsers,
    branch,
    user,
    useCoupon,
    usersBeforeSave,
    typeOfCouponToUse,
    title,
    informativeUser,
    useExistingOrShareCoupon,
    unregisteredClient,
  } = splitReservationForm(form, isUnregisteredClient);

  return {
    ...rest,
    branchId: branch?.id,
    userId: user?.id,
    ...(isCreating ? { reservationTypeId: reservationTypeForSelectedBranch?.id } : {}),
    ...(useExistingOrShareCoupon ? { userCouponId: form.userCouponId } : {}),
    ...(useCoupon && typeOfCouponToUse === 'new' ? { newCouponId: form.newCouponId } : {}),
    ...(isUnregisteredClient ? { unregisteredClient } : {}),
    ...(title ? { title } : {}),
    ...(informativeUser ? { informativeUserId: informativeUser.id } : {}),
    utcStartDate: form.localStartDate,
    utcEndDate: form.localEndDate,
    externalUserIds: isUnregisteredClient
      ? undefined
      : [...(externalUsers?.map((user) => user.id) ?? []), ...(usersBeforeSave?.map((user) => user.id) ?? [])],
  };
};
